import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Events.css';

const upcomingEvents = [
  {
    id: 2027,
    title: "Annual Gadaa Meeting 2027",
    date: "August 2027 (date to be confirmed)",
    time: "To be announced",
    location: "Bolton, Greater Manchester",
    description: "Save the date! Our next annual community meeting will take place in August 2027, featuring traditional Gadaa democratic processes, community discussions, cultural presentations, and collective decision-making. Full details will be shared soon.",
    image: "/Images/2026/gadaa-2026-4.jpg",
    category: "Meeting"
  }
];

const pastEvents = [
  {
    id: 2026,
    title: "Annual Gadaa Meeting 2026",
    date: "August 20, 2026",
    time: "10:00 AM - 6:00 PM",
    location: "Bolton, Greater Manchester",
    description: "Our 2026 annual meeting brought the Oromo community in the UK together for traditional Gadaa democratic processes, cultural presentations, and collective decision-making. Browse the photo gallery below to relive the day.",
    image: "/Images/2026/gadaa-2026-7.jpg",
    category: "Meeting",
    gallery: true
  },
  {
    id: 2025,
    title: "Annual Gadaa Meeting 2025",
    date: "August 20, 2025",
    time: "10:00 AM - 6:00 PM",
    location: "Bolton, Greater Manchester",
    description: "Our annual community meeting featured traditional Gadaa democratic processes, community discussions, cultural presentations, and collective decision-making. This important gathering brought together the entire Oromo community in the UK.",
    image: "/Images/2025/walgeeti_1.jpg",
    category: "Meeting"
  }
];

const gallery2026 = Array.from({ length: 8 }, (_, i) => `/Images/2026/gadaa-2026-${i + 1}.jpg`);

const gallery2025 = [
  '/Images/2025/image_3.jpeg',
  '/Images/2025/image_4.jpeg',
  '/Images/2025/hand.jpeg',
  '/Images/2025/together.jpeg',
  '/Images/2025/walgeeti_1.jpg'
];

const elderVideos = [
  {
    id: 'dida',
    name: 'Dida Guracha',
    src: '/videos/Dida Guracha.mp4',
    text: 'Elder Dida Guracha shares profound insights about the traditional Gadaa system and its relevance in modern times. He speaks about the importance of maintaining our democratic traditions and passing them on to future generations.'
  },
  {
    id: 'galgal',
    name: 'Galgal Boru',
    src: '/videos/Galgal_Boru.mp4',
    text: 'Elder Galgal Boru discusses the significance of community unity and the role of elders in guiding the younger generation. His speech emphasizes the values of respect, wisdom, and collective responsibility.'
  },
  {
    id: 'garbole',
    name: 'Garbole',
    src: '/videos/Garbole.mp4',
    text: 'Elder Garbole reflects on the journey of the Oromo community in the UK and the importance of preserving our cultural identity while adapting to new environments. His words inspire unity and cultural pride.'
  },
  {
    id: 'hassan',
    name: 'Hassan Abdullah',
    src: '/videos/HassanAbdullah.mp4',
    text: 'Elder Hassan Abdullah speaks about the challenges and opportunities facing our community. He emphasizes the importance of education, cultural preservation, and building bridges between generations.'
  }
];

const categories = [
  { title: 'Cultural Festivals', text: 'Traditional celebrations including Irreecha, Gadaa ceremonies, and seasonal festivals' },
  { title: 'Educational Programs', text: 'Language classes, cultural workshops, and knowledge sharing sessions' },
  { title: 'Community Meetings', text: 'Annual Gadaa meetings, democratic assemblies, decision-making sessions, and community discussions' },
  { title: 'Youth Activities', text: 'Mentorship programs and youth-focused events' },
  { title: 'Arts & Crafts', text: 'Traditional craft workshops, music sessions, and artistic expressions' },
  { title: 'Social Gatherings', text: 'Community meals, networking events, and informal social activities' }
];

const Events = () => {
  const [activeTab, setActiveTab] = useState('past');
  // { photos, title, index } while the photo viewer is open
  const [lightbox, setLightbox] = useState(null);
  const videoRefs = useRef({});

  const handleVideoPlay = (videoId) => {
    Object.keys(videoRefs.current).forEach(id => {
      if (id !== videoId && videoRefs.current[id]) {
        videoRefs.current[id].pause();
      }
    });
  };

  const openLightbox = (photos, title, index) => setLightbox({ photos, title, index });

  const stepPhoto = useCallback((step) => {
    setLightbox(lb => lb && { ...lb, index: (lb.index + step + lb.photos.length) % lb.photos.length });
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const isLightboxOpen = lightbox !== null;

  useEffect(() => {
    if (!isLightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') stepPhoto(-1);
      if (e.key === 'ArrowRight') stepPhoto(1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isLightboxOpen, stepPhoto, closeLightbox]);

  const currentEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  return (
    <>
      <section className="page-hero">
        <div className="container">
          <span className="eyebrow">What's on</span>
          <h1>Community Events</h1>
          <p>Stay connected with our vibrant community through cultural celebrations, educational programs, and social gatherings</p>
        </div>
      </section>

      <div className="events">
        <div className="container">
          <section className="events-tabs">
            <div className="tab-buttons">
              <button
                className={activeTab === 'upcoming' ? 'active' : ''}
                onClick={() => setActiveTab('upcoming')}
              >
                Upcoming Events
              </button>
              <button
                className={activeTab === 'past' ? 'active' : ''}
                onClick={() => setActiveTab('past')}
              >
                Past Events
              </button>
            </div>
          </section>

          <section className="events-content">
            <div className={`events-grid ${activeTab === 'past' ? 'past-events' : ''}`}>
              {currentEvents.map(event => (
                <div key={event.id} className="event-card card">
                  <div className="event-image">
                    <img src={event.image} alt={event.title} />
                    <div className="event-category">{event.category}</div>
                  </div>
                  <div className="event-details">
                    <h3>{event.title}</h3>
                    <div className="event-meta">
                      <div className="meta-item">
                        <strong>Date:</strong> {event.date}
                      </div>
                      <div className="meta-item">
                        <strong>Time:</strong> {event.time}
                      </div>
                      <div className="meta-item">
                        <strong>Location:</strong> {event.location}
                      </div>
                    </div>
                    <p className="event-description">{event.description}</p>
                    {event.gallery && (
                      <a className="event-gallery-link" href="#gallery-2026">View photo gallery &rarr;</a>
                    )}
                  </div>
                </div>
              ))}
              {activeTab === 'past' && (
                <button
                  type="button"
                  className="event-card card gallery-card"
                  onClick={() => openLightbox(gallery2025, 'Annual Gadaa Meeting 2025', 0)}
                >
                  <div className="gallery-card-mosaic">
                    {gallery2025.slice(0, 3).map(src => (
                      <img key={src} src={src} alt="" loading="lazy" />
                    ))}
                    <div className="event-category">Photos</div>
                  </div>
                  <div className="event-details">
                    <h3>2025 Photo Gallery</h3>
                    <p className="event-description">Moments from our Annual Gadaa Meeting 2025 in Bolton, Greater Manchester. Select to view the photos full size.</p>
                    <span className="event-gallery-link">View {gallery2025.length} photos &rarr;</span>
                  </div>
                </button>
              )}
            </div>
          </section>

          {activeTab === 'past' && (
            <section className="event-gallery" id="gallery-2026">
              <div className="section-head">
                <span className="eyebrow">Photo gallery</span>
                <h2>Annual Gadaa Meeting 2026</h2>
                <p>Moments from our 2026 gathering. Select a photo to view it full size.</p>
              </div>
              <div className="gallery-grid">
                {gallery2026.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    className="gallery-item"
                    onClick={() => openLightbox(gallery2026, 'Annual Gadaa Meeting 2026', i)}
                    aria-label={`Open photo ${i + 1} of ${gallery2026.length}`}
                  >
                    <img src={src} alt={`Annual Gadaa Meeting 2026, ${i + 1} of ${gallery2026.length}`} loading="lazy" />
                  </button>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'past' && (
            <section className="community-videos">
              <div className="section-head">
                <span className="eyebrow">Voices of our elders</span>
                <h2>Community Elder Speeches</h2>
                <p className="videos-intro">Watch our respected elders share their wisdom and insights about our community, traditions, and the importance of preserving our cultural heritage.</p>
              </div>
              <div className="videos-grid">
                {elderVideos.map(video => (
                  <div key={video.id} className="video-item card">
                    <div className="video-container">
                      <video
                        ref={el => { videoRefs.current[video.id] = el; }}
                        controls
                        preload="metadata"
                        onPlay={() => handleVideoPlay(video.id)}
                      >
                        <source src={video.src} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <div className="video-info">
                      <h3>{video.name}</h3>
                      <p>{video.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="event-categories">
            <div className="section-head">
              <span className="eyebrow">Something for everyone</span>
              <h2>Event Categories</h2>
            </div>
            <div className="categories-grid">
              {categories.map(c => (
                <div key={c.title} className="category-item card">
                  <h3>{c.title}</h3>
                  <p>{c.text}</p>
                </div>
              ))}
              {activeTab === 'past' && (
                <button
                  type="button"
                  className="event-card card gallery-card"
                  onClick={() => openLightbox(gallery2025, 'Annual Gadaa Meeting 2025', 0)}
                >
                  <div className="gallery-card-mosaic">
                    {gallery2025.slice(0, 3).map(src => (
                      <img key={src} src={src} alt="" loading="lazy" />
                    ))}
                    <div className="event-category">Photos</div>
                  </div>
                  <div className="event-details">
                    <h3>2025 Photo Gallery</h3>
                    <p className="event-description">Moments from our Annual Gadaa Meeting 2025 in Bolton, Greater Manchester. Select to view the photos full size.</p>
                    <span className="event-gallery-link">View {gallery2025.length} photos &rarr;</span>
                  </div>
                </button>
              )}
            </div>
          </section>

          <section className="event-info">
            <div className="cta-band">
              <div>
                <h2>Stay Updated</h2>
                <p>Don't miss out on our community events! Contact us to join our mailing list and receive regular updates about upcoming activities.</p>
              </div>
              <div className="cta-details">
                <p><strong>Email:</strong> oromogadaacommunity@gmail.com</p>
                <p><strong>Phone:</strong> +44 (0) 20 1234 5678</p>
                <p><strong>Follow us on social media for real-time updates</strong></p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {lightbox && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${lightbox.title} photos`}
          onClick={(e) => { if (e.target === e.currentTarget) closeLightbox(); }}
        >
          <button type="button" className="lightbox-close" onClick={closeLightbox} aria-label="Close">&times;</button>
          <button type="button" className="lightbox-prev" onClick={() => stepPhoto(-1)} aria-label="Previous photo">&#8592;</button>
          <img src={lightbox.photos[lightbox.index]} alt={`${lightbox.title}, ${lightbox.index + 1} of ${lightbox.photos.length}`} />
          <button type="button" className="lightbox-next" onClick={() => stepPhoto(1)} aria-label="Next photo">&#8594;</button>
          <span className="lightbox-count">{lightbox.title} · {lightbox.index + 1} / {lightbox.photos.length}</span>
        </div>
      )}
    </>
  );
};

export default Events;
